import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  ILmsCatalogPage,
  ILmsCatalogQuery,
  TLmsCatalogEntry,
  TLmsCourseLink,
  TLmsKeptContent,
  TLmsKeptContentDismissal,
  TLmsKeptContentDismissPayload,
  TLmsLinkPayload,
  TLmsLinkPreview,
} from "@/types/lms";

/** The shared `BasePaginator` envelope, narrowed to what the picker reads. */
interface ICatalogEnvelope {
  results: TLmsCatalogEntry[];
  next_page_results: boolean;
  total_count: number;
}

/** The project link endpoints (plan §6.5, NG-674).
 *
 *  **No vendor call anywhere here**, which is what makes the whole surface fast: linking
 *  is a single Cognota-side row, and the catalog is read from the cache the nightly pull
 *  fills. Every verb sits well inside the sub-1s read / sub-2.5s write budgets, so no
 *  caller needs a progress indicator and both writes are applied optimistically.
 */
export class LmsLinkService extends ApiService {
  async listLinks(
    slug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TLmsCourseLink[]> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/lms-links/`, config);
  }

  async linkProject(
    slug: string,
    projectId: string,
    payload: TLmsLinkPayload,
  ): Promise<TLmsCourseLink> {
    return this.post(`/api/workspaces/${slug}/projects/${projectId}/lms-links/`, payload);
  }

  /** What the requests behind this project already kept (NG-1157).
   *
   *  Its own call rather than a key on the link list: that response is the project's
   *  links and stays as narrow as a list response should be, while this one reaches
   *  across into intake. A page that only ever renders "Delivered by" should not pay for
   *  it.
   */
  async listKeptContent(
    slug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TLmsKeptContent[]> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/lms-kept-content/`, config);
  }

  /** What Cognota stores about one linked course or path, plus the link's own facts, for
   *  the Preview dialog on Delivery (NG-1858). No vendor call. */
  async getLinkPreview(
    slug: string,
    projectId: string,
    linkId: string,
    config?: ApiRequestConfig,
  ): Promise<TLmsLinkPreview> {
    return this.get(
      `/api/workspaces/${slug}/projects/${projectId}/lms-links/${linkId}/preview/`,
      config,
    );
  }

  /** Take one kept item off this project's list (NG-1891). The request's own Keep is
   *  untouched; this only hides the item here. Idempotent server-side. */
  async dismissKeptContent(
    slug: string,
    projectId: string,
    payload: TLmsKeptContentDismissPayload,
  ): Promise<TLmsKeptContentDismissal> {
    return this.post(
      `/api/workspaces/${slug}/projects/${projectId}/lms-kept-content/dismissals/`,
      payload,
    );
  }

  async unlinkProject(slug: string, projectId: string, linkId: string): Promise<void> {
    await this.delete(`/api/workspaces/${slug}/projects/${projectId}/lms-links/${linkId}/`);
  }

  /** A page of the catalog this project could link to.
   *
   *  Normalises the two shapes the endpoint answers with. A workspace holding no active
   *  connection gets a bare `[]` rather than an envelope, and collapsing that into
   *  "0 results" would tell somebody their catalog is empty when in fact nothing is
   *  connected — two different problems with two different fixes.
   */
  async listCatalog(
    slug: string,
    projectId: string,
    query: ILmsCatalogQuery = {},
    config?: ApiRequestConfig,
  ): Promise<ILmsCatalogPage> {
    const params = new URLSearchParams();
    if (query.kind) params.set("kind", query.kind);
    if (query.q) params.set("q", query.q);
    params.set("per_page", String(query.per_page ?? 50));

    const raw = await this.get<ICatalogEnvelope | TLmsCatalogEntry[]>(
      `/api/workspaces/${slug}/projects/${projectId}/lms-catalog/?${params.toString()}`,
      config,
    );

    if (Array.isArray(raw)) {
      return { entries: [], hasMore: false, totalCount: 0, hasActiveConnection: false };
    }
    return {
      entries: raw.results ?? [],
      hasMore: Boolean(raw.next_page_results),
      totalCount: raw.total_count ?? 0,
      hasActiveConnection: true,
    };
  }
}
